import { useMemo } from 'react'
import { PatientHistoryType } from 'src/graphql.generated'
import { merge } from 'lodash'
import { ApolloError } from '@apollo/client'
import { getFormFromLocalStorage } from './persistFormHelpers'
import { useGetPatientHistoryPrefillData } from './useGetPatientHistoryPrefillData'

type FormValuesState<T> = {
  values: T
  status: 'loading' | 'error' | 'initialized' | 'draft'
}

export function useLocalStorageWithPrefillValues<T>(
  uid: string,
  formType: PatientHistoryType,
  getDefaultValues: () => T,
  callID?: string | null
): FormValuesState<T> {
  const { data: prefillData, loading, error } = useGetPatientHistoryPrefillData(uid, formType, callID)

  const result = useMemo(() => {
    const values = getFormFromLocalStorage(uid, formType)

    if (values) {
      return { values, status: 'draft' as const }
    }

    const mergedValues = merge(getDefaultValues(), prefillData?.value)

    const status = getStatus(loading, error)

    return { values: mergedValues, status }
  }, [uid, formType, getDefaultValues, prefillData?.value, loading, error])

  return result
}

function getStatus(loading: boolean, error: ApolloError | undefined) {
  if (loading) {
    return 'loading' as const
  }
  if (error) {
    return 'error' as const
  }
  return 'initialized' as const
}
